import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ChatInteractiveSelection, ChatInteractiveSelectionType, ChatMessage, ChatMessageInteractiveOptions } from '../../models/chat-message.model';
import { ChatUiError } from '../../models/chat-ui-error.model';

type ChatResponseFeedback = 'good' | 'bad';
type ChatInteractiveSectionKey = keyof ChatMessageInteractiveOptions;

interface ChatInteractiveSection {
  key: ChatInteractiveSectionKey;
  label: string;
  type: ChatInteractiveSelectionType;
  items: string[];
}

@Component({
  selector: 'app-chat-thread',
  templateUrl: './chat-thread.component.html',
  styleUrls: ['./chat-thread.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChatThreadComponent implements OnChanges, OnDestroy {
  @ViewChild('threadContainer') threadContainer: ElementRef<HTMLDivElement>;
  @Input() messages: ChatMessage[] = [];
  @Input() isSending = false;
  @Input() error: ChatUiError | null = null;

  @Output() clearError = new EventEmitter<void>();
  @Output() retryMessage = new EventEmitter<ChatMessage>();
  @Output() feedbackSelected = new EventEmitter<{ message: ChatMessage; feedback: ChatResponseFeedback | null }>();
  @Output() interactiveOptionSelected = new EventEmitter<ChatInteractiveSelection>();

  copiedMessageId: string | null = null;
  private copyResetTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  trackByMessage(index: number, message: ChatMessage) {
    return message.id || index;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['messages']) {
      const schedule = typeof queueMicrotask === 'function' ? queueMicrotask : (cb: () => void) => setTimeout(cb, 0);
      schedule(() => this.scrollToBottom());
    }
  }

  ngOnDestroy(): void {
    if (this.copyResetTimer) {
      clearTimeout(this.copyResetTimer);
    }
  }

  copyResponse(message: ChatMessage): void {
    const content = message.content || '';
    if (!content.trim()) {
      return;
    }

    const markCopied = () => {
      this.copiedMessageId = message.id;
      if (this.copyResetTimer) {
        clearTimeout(this.copyResetTimer);
      }
      this.copyResetTimer = setTimeout(() => {
        this.copiedMessageId = null;
        this.cdr.markForCheck();
      }, 1600);
      this.cdr.markForCheck();
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(content).then(markCopied).catch(() => {
        this.copyWithFallback(content);
        markCopied();
      });
      return;
    }

    this.copyWithFallback(content);
    markCopied();
  }

  getFeedback(message: ChatMessage): ChatResponseFeedback | null {
    return (message.responseRating as ChatResponseFeedback | null) || null;
  }

  setFeedback(message: ChatMessage, feedback: ChatResponseFeedback): void {
    const currentFeedback = this.getFeedback(message);
    this.feedbackSelected.emit({ message, feedback: currentFeedback === feedback ? null : feedback });
  }

  retryResponse(message: ChatMessage): void {
    if (this.isSending) {
      return;
    }

    this.retryMessage.emit(message);
  }

  getInteractiveSections(message: ChatMessage): ChatInteractiveSection[] {
    const interactive = message.interactive;
    if (!interactive) {
      return [];
    }

    const sections: ChatInteractiveSection[] = [
      { key: 'products', type: 'product', label: 'Which product do you mean?', items: interactive.products || [] },
      { key: 'facilities', type: 'facility', label: 'Which facility do you mean?', items: interactive.facilities || [] },
      { key: 'woredas', type: 'woreda', label: 'Which woreda do you mean?', items: interactive.woredas || [] },
      { key: 'zones', type: 'zone', label: 'Which zone do you mean?', items: interactive.zones || [] },
      { key: 'hubs', type: 'hub', label: 'Which hub do you mean?', items: interactive.hubs || [] },
      { key: 'regions', type: 'region', label: 'Which region do you mean?', items: interactive.regions || [] },
    ];

    return sections.filter((section) => section.items.length > 0);
  }

  selectInteractiveOption(type: ChatInteractiveSelectionType, option: string): void {
    const trimmed = `${option || ''}`.trim();
    if (this.isSending || !trimmed) {
      return;
    }

    this.interactiveOptionSelected.emit({ type, value: trimmed });
  }

  get errorLabel() {
    switch (this.error?.kind) {
      case 'balance':
        return 'Balance';
      case 'timeout':
        return 'Timeout';
      case 'unavailable':
        return 'Unavailable';
      case 'rate_limit':
        return 'Rate limit';
      case 'conversation':
        return 'Conversation';
      case 'business_rule':
        return 'Chat';
      case 'server':
        return 'Server';
      default:
        return 'Error';
    }
  }

  private copyWithFallback(content: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = content;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }

  private scrollToBottom() {
    const element = this.threadContainer?.nativeElement;
    if (!element) {
      return;
    }

    const scrollRegion = element.closest('.chat-scroll-region') as HTMLElement | null;
    const scrollElement = scrollRegion || element;
    scrollElement.scrollTop = scrollElement.scrollHeight;
  }
}
